/**
 * Motion utilities — herbruikbare animatie helpers.
 *
 * Wraps `motion/animate` met sane defaults en automatische
 * `prefers-reduced-motion` handling. Beschikbaar voor child-themes via
 * de `@parent-js` Vite-alias zodat dezelfde transities consistent
 * werken over parent en child.
 *
 * Standaard easing en duration zijn afgestemd op structurele UI-
 * animaties (slide-in van een drawer, sticky header). Voor andere
 * timings kunnen callers de `options` overrulen.
 *
 * @module modules/ui/motion-utils
 */

import { animate } from 'motion';

/**
 * Detecteert de OS-level reduced-motion preference. Wordt door
 * {@see motionAnimate} gebruikt om de duration op 0 te clampen — geen
 * visuele beweging maar de Promise-flow blijft intact zodat
 * await-ketens normaal doorlopen.
 *
 * @returns {boolean}
 */
export function prefersReducedMotion() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Animeer een element met `motion/animate`. Respecteert
 * `prefers-reduced-motion` automatisch door duration op 0 te clampen.
 *
 * @param {Element} element
 * @param {Record<string, unknown>} keyframes  Motion.js keyframes.
 * @param {Record<string, unknown>} [options]  Motion.js options.
 *
 * @returns {Promise<unknown>} Animation-promise.
 */
export function motionAnimate(element, keyframes, options = {}) {
    const requested = typeof options.duration === 'number' ? options.duration : 0.3;
    const duration  = prefersReducedMotion() ? 0 : requested;
    const easing    = options.easing ?? 'ease-out';

    return animate(element, keyframes, { ...options, duration, easing });
}

/**
 * Slide een element in vanaf een richting. Zet `hidden = false` en
 * `aria-hidden = "false"` voor de animatie zodat het element direct
 * focusable en in-tree is.
 *
 * @param {Element} element
 * @param {'left'|'right'|'top'|'bottom'} from  Richting waaruit het
 *                                              element binnenschuift.
 * @param {Record<string, unknown>} [options]
 *
 * @returns {Promise<unknown>}
 */
export function slideIn(element, from = 'right', options = {}) {
    const axis       = from === 'left' || from === 'right' ? 'X' : 'Y';
    const startValue = from === 'right' || from === 'bottom' ? '100%' : '-100%';

    if (element instanceof HTMLElement) {
        element.hidden = false;
        element.setAttribute('aria-hidden', 'false');
    }

    return motionAnimate(
        element,
        { transform: [`translate${axis}(${startValue})`, `translate${axis}(0)`] },
        { duration: 0.3, easing: 'ease-out', ...options },
    );
}

/**
 * Slide een element uit naar een richting. Wacht op de animatie en
 * zet daarna `hidden = true` + `aria-hidden = "true"` zodat het
 * element uit de tab-volgorde verdwijnt.
 *
 * @param {Element} element
 * @param {'left'|'right'|'top'|'bottom'} to  Richting waarin het
 *                                            element wegschuift.
 * @param {Record<string, unknown>} [options]
 *
 * @returns {Promise<void>}
 */
export async function slideOut(element, to = 'right', options = {}) {
    const axis     = to === 'left' || to === 'right' ? 'X' : 'Y';
    const endValue = to === 'right' || to === 'bottom' ? '100%' : '-100%';

    await motionAnimate(
        element,
        { transform: [`translate${axis}(0)`, `translate${axis}(${endValue})`] },
        { duration: 0.25, easing: 'ease-in', ...options },
    );

    if (element instanceof HTMLElement) {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
    }
}

/**
 * Fade een element in van opacity 0 naar 1.
 *
 * @param {Element} element
 * @param {Record<string, unknown>} [options]
 *
 * @returns {Promise<unknown>}
 */
export function fadeIn(element, options = {}) {
    if (element instanceof HTMLElement) {
        element.hidden = false;
        element.setAttribute('aria-hidden', 'false');
    }

    return motionAnimate(
        element,
        { opacity: [0, 1] },
        { duration: 0.2, ...options },
    );
}

/**
 * Fade een element uit naar opacity 0 en verberg het na de animatie.
 *
 * @param {Element} element
 * @param {Record<string, unknown>} [options]
 *
 * @returns {Promise<void>}
 */
export async function fadeOut(element, options = {}) {
    await motionAnimate(
        element,
        { opacity: [1, 0] },
        { duration: 0.2, ...options },
    );

    if (element instanceof HTMLElement) {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
    }
}
